import React from "react";
import { motion } from "framer-motion";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
    <div className="text-gray-600 leading-relaxed space-y-3 text-sm">{children}</div>
  </section>
);

const PrivacyPage = () => (
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <div className="mb-10">
        <p className="text-brand-600 text-sm font-semibold uppercase tracking-wider mb-2">Legal</p>
        <h1 className="text-4xl font-black text-gray-900 mb-3">Privacy Policy</h1>
        <p className="text-gray-500">Last updated: January 1, 2025</p>
      </div>

      <div className="card p-8 md:p-12">
        <Section title="1. Introduction">
          <p>TS ELITE SOURCE LLC, operating storeonline.online ("we," "our," or "us"), is committed to protecting your personal information and your right to privacy. This Privacy Policy describes how we collect, use, and share information about you when you use our website at storeonline.online (the "Site").</p>
          <p>Please read this policy carefully. If you disagree with its terms, please discontinue use of our Site.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Information you provide directly:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Contact information (name, email address, phone number)</li>
            <li>Shipping and billing addresses</li>
            <li>Payment information (credit card details — processed securely, not stored on our servers)</li>
            <li>Order history and purchase records</li>
            <li>Customer support communications</li>
          </ul>
          <p><strong>Information collected automatically:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>IP address and approximate location</li>
            <li>Pages visited and time spent on the Site</li>
            <li>Referring website information</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Process and fulfill your orders</li>
            <li>Send order confirmations, shipping updates, and invoices</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Send marketing communications (with your consent)</li>
            <li>Improve our Site, products, and services</li>
            <li>Detect and prevent fraud or unauthorized transactions</li>
            <li>Comply with legal obligations</li>
          </ul>
        </Section>

        <Section title="4. Sharing Your Information">
          <p>We do not sell, trade, or rent your personal information to third parties for their marketing purposes. We may share your information with:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Shipping carriers</strong> (UPS, FedEx, USPS) to deliver your orders</li>
            <li><strong>Payment processors</strong> to securely process transactions</li>
            <li><strong>Analytics providers</strong> to help us understand Site usage (data is anonymized)</li>
            <li><strong>Legal authorities</strong> when required by law or to protect our rights</li>
          </ul>
        </Section>

        <Section title="5. Cookies and Tracking Technologies">
          <p>We use cookies and similar tracking technologies to enhance your experience on our Site. Types of cookies we use:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Essential cookies:</strong> Required for the Site to function (e.g., your shopping cart)</li>
            <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with the Site</li>
            <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
          </ul>
          <p>You can control cookie settings through your browser settings. Note that disabling some cookies may affect the functionality of the Site.</p>
        </Section>

        <Section title="6. Local Storage">
          <p>Our Site uses browser localStorage to save your shopping cart and order information locally on your device. This data remains on your device and is not transmitted to our servers unless you place an order.</p>
          <p>You can clear this data at any time by clearing your browser's local storage through your browser settings.</p>
        </Section>

        <Section title="7. Data Security">
          <p>We implement industry-standard security measures to protect your personal information, including SSL/TLS encryption for all data transmission. However, no method of internet transmission is 100% secure, and we cannot guarantee absolute security.</p>
          <p>We do not store full credit card numbers on our servers. Payment processing is handled by PCI-DSS compliant payment processors.</p>
        </Section>

        <Section title="8. Your Rights">
          <p>Depending on your location, you may have the following rights regarding your personal data:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
          </ul>
          <p>To exercise any of these rights, contact us at privacy@storeonline.online.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>Our Site is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this Privacy Policy periodically. We will notify you of significant changes by posting the new policy on this page with an updated "Last Updated" date. Your continued use of the Site after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact Us">
          <p>For questions, concerns, or requests regarding this Privacy Policy:</p>
          <p>
            <strong>TS ELITE SOURCE LLC</strong><br />
            5752 Keron Ln, Grovetown GA 30813<br />
            Email: privacy@storeonline.online<br />
            Support: info@e-storeonline.online
          </p>
        </Section>
      </div>
    </motion.div>
  </div>
);

export default PrivacyPage;
